import React, { useEffect, useRef, useState } from 'react';
import dropdownIcon from '../../assets/icons/downArrow.svg';
import { useField } from 'formik';

interface MultiSelectionDropDownProps {
	options: { id: string; value: string }[];
	onOptionChange: (selectedIds: string[]) => void;
	_selectedFilters: string[];
	name: string;
	showListOnSelectAll?: boolean;
	minSelectedItemsToShow?: number;
	customMessageForSelectAll?: string;
	customMessageForDeselectAll?: string;
	containerClassName?: string;
	childContainerClassName?: string;
	selectedItemClassName?: string;
	isAllCheckRequired?: boolean;
}

const MultiSelectDropDown: React.FC<MultiSelectionDropDownProps> = ({
	options,
	onOptionChange,
	_selectedFilters,
	name,
	showListOnSelectAll = true,
	minSelectedItemsToShow = 2,
	customMessageForSelectAll,
	customMessageForDeselectAll,
	containerClassName,
	childContainerClassName,
	selectedItemClassName,
	isAllCheckRequired = true,
}) => {
	const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
	const [dropdownOpen, setDropdownOpen] = useState(false);
	const dropdownButtonRef = useRef<HTMLButtonElement>(null);
	const allCheckRef = useRef<HTMLInputElement>(null);
	const [inputSearch, setInputSearch] = useState('');
	const dropdownRef = useRef<HTMLUListElement>(null);
	const [field, meta, helpers] = useField(name);

	useEffect(() => {
		setSelectedOptions(field.value);
		if (isAllCheckRequired && allCheckRef.current) {
			if (_selectedFilters.length == options.length) {
				allCheckRef.current.checked = true;
				allCheckRef.current.indeterminate = false;
			} else if (_selectedFilters.filter((x) => x != '').length > 0) {
				allCheckRef.current.indeterminate = true;
			} else {
				allCheckRef.current.checked = false;
				allCheckRef.current.indeterminate = false;
			}
		}
	}, [_selectedFilters, field.value, isAllCheckRequired]);

	useEffect(() => {
		const handleClose = (e: MouseEvent) => {
			if (
				dropdownOpen &&
				!(
					e.target === dropdownRef.current ||
					e.target === dropdownButtonRef.current ||
					dropdownButtonRef.current?.contains(e.target as Node) ||
					dropdownRef.current?.contains(e.target as Node)
				)
			) {
				toggleDropdown();
			}
		};
		if (dropdownOpen) {
			document.addEventListener('click', handleClose);
		} else {
			document.removeEventListener('click', handleClose);
		}
		return () => document.removeEventListener('click', handleClose);
	}, [dropdownRef, dropdownOpen]);

	const handleOptionToggle = (optionId: string) => {
		if (allCheckRef.current) {
			allCheckRef.current.indeterminate = true;
			allCheckRef.current.checked = false;
		}
		if (selectedOptions?.includes(optionId)) {
			const filteredSelectedOptions = selectedOptions?.filter(
				(id) => id !== optionId,
			);
			if (filteredSelectedOptions?.length == 0 && allCheckRef.current) {
				allCheckRef.current.indeterminate = false;
				allCheckRef.current.checked = false;
			}
			setSelectedOptions(filteredSelectedOptions);
			helpers.setValue(filteredSelectedOptions);
			onOptionChange(filteredSelectedOptions);
		} else {
			if (
				(selectedOptions?.length ?? 0) + 1 == options.length &&
				allCheckRef.current
			) {
				allCheckRef.current.checked = true;
				allCheckRef.current.indeterminate = false;
			}
			setSelectedOptions([...(selectedOptions || []), optionId]);
			helpers.setValue([...(selectedOptions || []), optionId]);
			onOptionChange([...(selectedOptions || []), optionId]);
		}
	};

	const toggleDropdown = () => {
		setDropdownOpen(!dropdownOpen);
		setInputSearch('');
	};

	const handleInputSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setInputSearch(e.target.value);
	};

	const handleAllSelect = () => {
		if (!allCheckRef.current?.checked) {
			if (inputSearch) {
				const updatedOptions = options
					?.filter(
						(opt) =>
							opt.value
								.toUpperCase()
								.indexOf(inputSearch.toUpperCase()) !== -1,
					)
					.map((opt) => opt.id);
				setSelectedOptions(updatedOptions);
				helpers.setValue(updatedOptions);
				onOptionChange(updatedOptions);
			} else {
				const updatedOptions = options.map((opt) => opt.id);
				setSelectedOptions(updatedOptions);
				helpers.setValue(updatedOptions);
				onOptionChange(updatedOptions);
			}

			if (allCheckRef.current) {
				allCheckRef.current.checked = true;
				allCheckRef.current.indeterminate = false;
			}
		} else {
			if (allCheckRef.current) {
				setSelectedOptions([]);
				helpers.setValue([]);
				onOptionChange([]);
				allCheckRef.current.checked = false;
				allCheckRef.current.indeterminate = false;
			}
		}
	};

	const hasOptions = () => {
		return (
			options?.filter(
				(opt) =>
					opt.value
						.toUpperCase()
						.indexOf(inputSearch.toUpperCase()) !== -1,
			).length > 0
		);
	};

	const showSelectedList = () => (
		<>
			{selectedOptions
				?.slice(0, minSelectedItemsToShow)
				?.map((selectedId) => (
					<span
						key={selectedId}
						title={
							options?.find((option) => option.id === selectedId)
								?.value
						}
						className={`max-w-[120px] overflow-hidden whitespace-nowrap text-ellipsis bg-rcrm-primary-border-blue px-2 rounded-sm ${selectedItemClassName ?? ''}`}
					>
						{
							options?.find((option) => option.id === selectedId)
								?.value
						}
					</span>
				))}
			{selectedOptions?.slice(minSelectedItemsToShow)?.length > 0 && (
				<span
					className={`bg-rcrm-primary-border-blue px-2 rounded-sm `}
				>
					+{selectedOptions?.slice(minSelectedItemsToShow)?.length}
				</span>
			)}
		</>
	);

	return (
		<div
			className={`relative w-full text-sm bg-white rounded-lg ${containerClassName ? containerClassName : ''}`}
		>
			<button
				onClick={toggleDropdown}
				ref={dropdownButtonRef}
				className={`border border-rcrm-primary-border-blue text-gray-700 p-3 flex flex-wrap w-full
				 justify-between items-center ${childContainerClassName ? childContainerClassName : ''}`}
			>
				<div className="flex flex-wrap items-center gap-2">
					{selectedOptions?.length ? (
						<>
							{selectedOptions?.length < options?.length ? (
								showSelectedList()
							) : (
								<>
									{showListOnSelectAll
										? showSelectedList()
										: customMessageForSelectAll}
								</>
							)}
						</>
					) : (
						<div className="flex w-full justify-between">
							{customMessageForDeselectAll || 'Select Options'}
						</div>
					)}
				</div>
				<div className="flex justify-center items-center">
					<img src={dropdownIcon} alt="desc" className="w-3 h-3" />
				</div>
			</button>
			{dropdownOpen && (
				<ul
					className={`absolute ${dropdownOpen ? '' : 'hidden'} z-20 w-full text-gray-700
				 pb-1 pl-0 mt-2 max-h-[50vh] min-h-[48px] overflow-auto bg-white border border-gray-300 rounded shadow-lg`}
					ref={dropdownRef}
				>
					<li className="px-2 py-2 sticky top-0 bg-white ">
						<input
							placeholder="Search"
							type="text"
							value={inputSearch}
							onChange={handleInputSearch}
							className=" border border-gray-300 p-2 hover:outline-none hover:border-gray-400 w-full rounded-sm outline-none focus:border-gray-500 text-gray-700"
						/>
					</li>
					{isAllCheckRequired ? (
						<li
							onClick={() => handleAllSelect()}
							className={`cursor-pointer hover:bg-gray-200 ${hasOptions() ? 'py-2' : ''} px-4`}
						>
							{hasOptions() ? (
								<>
									<input
										type="checkbox"
										className="form-checkbox h-3 w-3"
										ref={allCheckRef}
										checked={allCheckRef.current?.checked}
										onChange={handleAllSelect}
										readOnly
									/>

									<span className="ml-2">
										{!allCheckRef.current?.checked ||
										allCheckRef.current?.indeterminate
											? customMessageForSelectAll ||
												'Select all'
											: customMessageForDeselectAll ||
												'Deselect all'}
									</span>
								</>
							) : null}
						</li>
					) : null}
					{options
						?.filter(
							(opt) =>
								opt.value
									.toUpperCase()
									.indexOf(inputSearch.toUpperCase()) !== -1,
						)
						?.map((option) => (
							<li
								key={option.id}
								onClick={() => handleOptionToggle(option.id)}
								className="cursor-pointer hover:bg-gray-200 py-2 px-4"
							>
								<input
									type="checkbox"
									className="form-checkbox h-3 w-3"
									checked={selectedOptions?.includes(
										option.id,
									)}
									readOnly
								/>
								<span className="ml-2">{option.value}</span>
							</li>
						))}
				</ul>
			)}
		</div>
	);
};

export default MultiSelectDropDown;
